import { useEffect, useState } from "react";
import { db, storage } from "../../firebase";
import {
  doc,
  updateDoc,
  addDoc,
  collection,
  serverTimestamp,
  increment,
} from "firebase/firestore";
import { ref, uploadBytesResumable, getDownloadURL } from "firebase/storage";
import { toast } from "react-hot-toast";

const MAX_MB = 15;
const ACCEPTED = "application/pdf,image/*";

export default function PayAgentFeesModal({ open, onClose, agent }) {
  const [amount, setAmount] = useState(0);
  const [file, setFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [pct, setPct] = useState(0);
  const [url, setUrl] = useState("");

  useEffect(() => {
    if (open) {
      setAmount(Math.max(0, Number(agent?.feeOutstandingUSD || 0)).toFixed(2));
      setFile(null);
      setUploading(false);
      setPct(0);
      setUrl("");
    }
  }, [open, agent]);

  if (!open) return null;

  const pickFile = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (f.size > MAX_MB * 1024 * 1024) return toast.error(`Max ${MAX_MB}MB`);
    setFile(f);
  };

  const upload = async () => {
    if (!file) return toast("Choose a file");
    try {
      setUploading(true);
      setPct(0);
      const ext = file.name.split(".").pop()?.toLowerCase() || "dat";
      const path = `agent_fees/${agent?.id}/proof_${Date.now()}.${ext}`;
      const r = ref(storage, path);
      const t = uploadBytesResumable(r, file, {
        contentType: file.type,
        cacheControl: "public,max-age=31536000",
      });
      t.on("state_changed", (s) =>
        setPct(Math.round((s.bytesTransferred / s.totalBytes) * 100))
      );
      await t;
      setUrl(await getDownloadURL(r));
      toast.success("Uploaded");
    } catch (e) {
      console.error(e);
      toast.error("Upload failed");
    } finally {
      setUploading(false);
    }
  };

  const submit = async () => {
    const amt = Number(amount || 0);
    if (!amt || amt <= 0) return toast.error("Enter amount");
    try {
      // create payment request
      await addDoc(collection(db, "agent_fee_payments"), {
        agentUid: agent?.id,
        amountUSD: amt,
        proofUrl: url || "",
        status: "submitted",
        createdAt: serverTimestamp(),
      });
      // mark pending on agent doc (for banner)
      await updateDoc(doc(db, "agents", agent?.id), {
        feePendingUSD: increment(amt),
      });
      toast.success("Fee payment submitted for admin approval");
      onClose?.();
    } catch (e) {
      console.error(e);
      toast.error("Submit failed");
    }
  };

  return (
    <div className="fixed inset-0 z-[160]">
      <div className="absolute inset-0 bg-black/60" onClick={onClose} />
      <div className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 w-[92vw] max-w-md bg-slate-800 rounded-2xl p-5">
        <h3 className="text-lg font-bold mb-3">Pay Agent Card Fees</h3>
        <div className="text-sm text-slate-300 mb-2">
          Outstanding:{" "}
          <b>${Number(agent?.feeOutstandingUSD || 0).toFixed(2)}</b> · Pending:{" "}
          <b>${Number(agent?.feePendingUSD || 0).toFixed(2)}</b>
        </div>

        <label className="block mb-3">
          <div className="text-sm text-slate-300 mb-1">Amount (USD)</div>
          <div className="flex">
            <span className="inline-flex items-center px-3 rounded-l bg-slate-700 text-slate-200">
              $
            </span>
            <input
              className="w-full px-3 py-2 rounded-r bg-slate-700 text-white"
              value={amount}
              onChange={(e) => setAmount(e.target.value)}
            />
          </div>
        </label>

        <div className="mb-2">
          <div className="text-sm text-slate-300 mb-1">
            Upload proof (image/PDF)
          </div>
          <input
            type="file"
            accept={ACCEPTED}
            onChange={pickFile}
            className="w-full file:mr-3 file:px-3 file:py-2 file:rounded file:bg-slate-600 file:text-white file:border-0 file:cursor-pointer bg-slate-700 text-white p-1 rounded"
          />
          {file && (
            <div className="text-xs text-slate-400 mt-1">{file.name}</div>
          )}
          <button
            onClick={upload}
            className="mt-2 px-3 py-2 rounded bg-indigo-400 text-black font-semibold disabled:opacity-50"
            disabled={!file || uploading}
          >
            {uploading ? `Uploading… ${pct}%` : "Upload"}
          </button>
          {url && (
            <div className="mt-2 text-xs">
              <a
                className="underline"
                href={url}
                target="_blank"
                rel="noreferrer"
              >
                View uploaded file
              </a>
            </div>
          )}
        </div>

        <div className="mt-4 flex items-center justify-end gap-2">
          <button onClick={onClose} className="px-3 py-2 rounded bg-slate-700">
            Cancel
          </button>
          <button
            onClick={submit}
            className="px-4 py-2 rounded bg-lime-400 text-black font-semibold"
          >
            Submit
          </button>
        </div>
      </div>
    </div>
  );
}
